import { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, Eye, Users, BookOpen, Rss, Globe } from 'lucide-react';
import api from '../utils/api';

export default function AnalyticsTab({ headers }) {
  const [overview, setOverview] = useState(null);
  const [pageviews, setPageviews] = useState([]);
  const [signups, setSignups] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pv, su, tp, rf] = await Promise.all([
        api.get('/admin/analytics/overview', { headers }),
        api.get('/admin/analytics/pageviews', { headers }),
        api.get('/admin/analytics/signups', { headers }),
        api.get('/admin/analytics/top-pages', { headers }),
        api.get('/admin/analytics/referrers', { headers }),
      ]);
      setOverview(ov.data);
      setPageviews(pv.data);
      setSignups(su.data);
      setTopPages(tp.data);
      setReferrers(rf.data);
    } catch (e) {
      console.error('Analytics fetch error:', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <p className="text-muted-foreground text-sm">Loading analytics...</p>;

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {[
            { label: 'Views Today', value: overview.views_today, icon: Eye },
            { label: 'Views This Week', value: overview.views_week, icon: TrendingUp },
            { label: 'Views This Month', value: overview.views_month, icon: Globe },
            { label: 'Signups (30d)', value: overview.signups_month, icon: Users },
            { label: 'Club Joins (7d)', value: overview.club_joins_week, icon: BookOpen },
          ].map((k, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-4" data-testid={`kpi-${k.label.toLowerCase().replace(/[\s()]/g, '-')}`}>
              <k.icon className="w-4 h-4 text-primary mb-1" />
              <p className="font-serif text-2xl text-primary">{k.value}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{k.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Page Views Chart */}
      <div className="bg-white/5 border border-white/10 rounded-sm p-6">
        <h3 className="font-serif text-lg mb-4">Page Views <span className="text-muted-foreground text-xs font-sans">(Last 30 days)</span></h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={pageviews}>
            <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 10 }} interval={4} />
            <YAxis tick={{ fill: '#666', fontSize: 10 }} />
            <Tooltip
              contentStyle={{ background: '#121212', border: '1px solid #333', borderRadius: 4, fontSize: 12 }}
              labelStyle={{ color: '#999' }}
              itemStyle={{ color: '#D4AF37' }}
            />
            <Bar dataKey="views" fill="#D4AF37" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Signups Chart */}
      <div className="bg-white/5 border border-white/10 rounded-sm p-6">
        <h3 className="font-serif text-lg mb-4">Signups & Club Joins <span className="text-muted-foreground text-xs font-sans">(Last 30 days)</span></h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={signups}>
            <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 10 }} interval={4} />
            <YAxis tick={{ fill: '#666', fontSize: 10 }} />
            <Tooltip
              contentStyle={{ background: '#121212', border: '1px solid #333', borderRadius: 4, fontSize: 12 }}
              labelStyle={{ color: '#999' }}
            />
            <Line type="monotone" dataKey="users" stroke="#D4AF37" strokeWidth={2} dot={false} name="Users" />
            <Line type="monotone" dataKey="members" stroke="#3b82f6" strokeWidth={2} dot={false} name="Club Members" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-white/5 border border-white/10 rounded-sm p-6">
          <h3 className="font-serif text-lg mb-4">Top Pages</h3>
          {topPages.length === 0 ? (
            <p className="text-sm text-muted-foreground">No page view data yet. Views will appear as visitors browse the site.</p>
          ) : (
            <div className="space-y-2">
              {topPages.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground truncate max-w-[200px]">{p.path}</span>
                  <span className="text-primary font-semibold">{p.views}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Referrers */}
        <div className="bg-white/5 border border-white/10 rounded-sm p-6">
          <h3 className="font-serif text-lg mb-4">Top Referrers</h3>
          {referrers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No referrer data yet.</p>
          ) : (
            <div className="space-y-2">
              {referrers.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground truncate max-w-[200px]">{r.referrer}</span>
                  <span className="text-primary font-semibold">{r.views}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RSS Link */}
      <div className="bg-white/5 border border-white/10 rounded-sm p-4 flex items-center gap-3">
        <Rss className="w-4 h-4 text-primary" />
        <div>
          <p className="text-sm font-semibold">Blog RSS Feed</p>
          <a href="/api/blog/rss" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline" data-testid="rss-feed-link">
            aighostwriterpro.com/api/blog/rss
          </a>
        </div>
      </div>
    </div>
  );
}
